import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {forkJoin, of, Subject} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {CropYieldForecast} from '@6grain/masofa-monolith-api-client/ai-inference';
import {Field, FieldService, Season, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {Crop, Region} from '@6grain/masofa-monolith-api-client/dictionaries';
import {CropService} from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import {Variety} from '@6grain/masofa-monolith-api-client/dictionaries/model/variety';
import {VarietyService} from '@6grain/masofa-monolith-api-client/dictionaries/api/variety.service';
import {ServerTranslateService} from '@app/services/server-translate.service';
import {FieldDetailsDialogComponent} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/field-details-dialog.component';
import {
  FieldDetailsView,
  YIELD_FORECAST_FIELD_DETAILS_TABS,
} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/field-details-view';
import {
  buildYieldForecastGeneral,
  resolveWeatherPoint,
  YieldForecastGeneralModel,
} from './yield-forecast-general.util';

interface ForecastBundle {
  season: Season | null;
  variety: Variety | null;
  crop: Crop | null;
  fieldPolygon: string | null;
}

const EMPTY_BUNDLE: ForecastBundle = {season: null, variety: null, crop: null, fieldPolygon: null};

@Component({
  selector: 'app-crop-yield-forecast-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FieldDetailsDialogComponent,
  ],
  templateUrl: './crop-yield-forecast-detail-dialog.component.html',
  styleUrl: './crop-yield-forecast-detail-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CropYieldForecastDetailDialogComponent {
  private readonly seasonService = inject(SeasonService);
  private readonly varietyService = inject(VarietyService);
  private readonly fieldService = inject(FieldService);
  private readonly cropService = inject(CropService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly translate = inject(TranslateService);
  private readonly requests$ = new Subject<CropYieldForecast | null>();
  private loadedKey = '';

  readonly visible = input(false);
  readonly forecast = input<CropYieldForecast | null>(null);
  readonly fieldName = input('');
  readonly crop = input<Crop | null>(null);
  readonly region = input<Region | null>(null);
  readonly district = input<Region | null>(null);
  readonly closed = output<void>();

  readonly loading = signal(false);
  private readonly bundle = signal<ForecastBundle>(EMPTY_BUNDLE);
  private readonly lang = signal(this.translate.currentLang || 'ru-RU');

  readonly general = computed(() => this.buildGeneral());
  readonly fieldView = computed(() => this.toFieldView());
  readonly fieldId = computed(() => this.forecast()?.fieldId || this.bundle().season?.fieldId || null);
  readonly plantingRaw = computed(() => this.bundle().season?.plantingDate || this.bundle().season?.plantingDatePlan || null);
  readonly harvestRaw = computed(() => this.bundle().season?.harvestingDate || this.bundle().season?.harvestingDatePlan || null);
  readonly weatherPoint = computed(() => resolveWeatherPoint(this.bundle().season, this.bundle().fieldPolygon));
  readonly tabCrops = computed(() => {
    const crop = this.crop() ?? this.bundle().crop;
    return crop?.id ? [crop] : [];
  });
  readonly seasonId = computed(() => this.bundle().season?.id || this.forecast()?.seasonId || null);
  readonly seasonCropId = computed(() => this.bundle().season?.cropId || this.forecast()?.cropId || null);

  private readonly details = viewChild(FieldDetailsDialogComponent);
  private openedKey = '';

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((event) => this.lang.set(event.lang));

    this.requests$
      .pipe(
        switchMap((forecast) => this.fetch(forecast)),
        takeUntilDestroyed(),
      )
      .subscribe((bundle) => {
        this.bundle.set(bundle);
        this.loading.set(false);
      });

    effect(() => {
      const key = this.openKey();
      if (key === this.loadedKey) {
        return;
      }
      this.loadedKey = key;
      untracked(() => this.startLoad());
    });

    effect(() => {
      const key = this.openKey();
      const details = this.details();
      untracked(() => this.syncDialog(key, details));
    });
  }

  onVisibleChange(open: boolean): void {
    if (!open) {
      this.closed.emit();
    }
  }

  onInnerClose(): void {
    this.onVisibleChange(false);
  }

  private openKey(): string {
    if (!this.visible()) {
      return '';
    }
    const forecast = this.forecast();
    return [forecast?.id, forecast?.seasonId, forecast?.fieldId, forecast?.forecastDate].join('|');
  }

  private startLoad(): void {
    const forecast = this.visible() ? this.forecast() : null;
    this.bundle.set(EMPTY_BUNDLE);
    this.loading.set(!!forecast?.seasonId);
    this.requests$.next(forecast);
  }

  private fetch(forecast: CropYieldForecast | null) {
    if (!forecast?.seasonId || !this.visible()) {
      return of(EMPTY_BUNDLE);
    }
    return this.seasonService.cropMonitoringSeasonGetByIdIdGet(forecast.seasonId).pipe(
      switchMap((season) => {
        const variety$ = season?.varietyId
          ? this.varietyService.dictionariesVarietyGetByIdIdGet(season.varietyId).pipe(catchError(() => of(null)))
          : of(null);
        const crop$ = this.crop() || !forecast.cropId
          ? of(this.crop())
          : this.cropService.dictionariesCropGetByIdIdGet(forecast.cropId).pipe(catchError(() => of(null)));
        const fieldId = forecast.fieldId || season?.fieldId;
        const field$ = !resolveWeatherPoint(season, null) && fieldId
          ? this.fieldService.cropMonitoringFieldGetByIdIdGet(fieldId).pipe(catchError(() => of(null)))
          : of(null);
        return forkJoin({season: of(season), variety: variety$, crop: crop$, field: field$});
      }),
      map(({season, variety, crop, field}) => ({
        season,
        variety,
        crop,
        fieldPolygon: polygonOf(field),
      })),
      catchError(() => of(EMPTY_BUNDLE)),
    );
  }

  private syncDialog(key: string, details: FieldDetailsDialogComponent | undefined): void {
    if (!key) {
      this.openedKey = '';
      details?.dismissQuiet();
      return;
    }
    if (!details || key === this.openedKey) {
      return;
    }
    this.openedKey = key;
    const fieldId = this.forecast()?.fieldId || null;
    details.show(fieldId ? {id: fieldId} : {});
  }

  private toFieldView(): FieldDetailsView {
    const general = this.general();
    const point = this.weatherPoint();
    return {
      id: 'yield-forecast',
      tabs: YIELD_FORECAST_FIELD_DETAILS_TABS,
      startTab: 'general',
      showMore: false,
      dismissableMask: true,
      atmShowAdd: false,
      anomaliesOpenPassport: true,
      indicesChrome: false,
      meteoSlot: 'season',
      loading: this.loading(),
      header: {title: general.title, subtitle: general.subtitle},
      general,
      indicesChart: {
        seasonId: this.seasonId() || '',
        cropId: this.seasonCropId(),
        plantingRaw: this.plantingRaw(),
        harvestRaw: this.harvestRaw(),
      },
      seasonWeather: {
        latitude: point?.lat ?? null,
        longitude: point?.lng ?? null,
        periodStart: this.plantingRaw(),
        periodEnd: this.harvestRaw(),
      },
      crops: this.tabCrops(),
    };
  }

  private buildGeneral(): YieldForecastGeneralModel {
    this.lang();
    const bundle = this.bundle();
    const crop = this.crop() ?? bundle.crop;
    return buildYieldForecastGeneral({
      forecast: this.forecast(),
      season: bundle.season,
      cropName: this.serverTranslate.getDisplayValue(crop, 'names'),
      varietyName: this.serverTranslate.getDisplayValue(bundle.variety, 'names'),
      fieldName: this.fieldName(),
      regionName: this.serverTranslate.getDisplayValue(this.region(), 'names'),
      districtName: this.serverTranslate.getDisplayValue(this.district(), 'names'),
    });
  }
}

function polygonOf(field: Field | {entity?: Field} | null): string | null {
  if (!field) {
    return null;
  }
  const wrapped = field as {entity?: Field};
  const row: Field = wrapped.entity ?? (field as Field);
  return row.polygonJson ?? null;
}
